<template>
  <v-card class="assign-modal" elevation="0">
    <div class="assign-header">
      <div class="header-icon"><i class="fas fa-car"></i></div>
      <div class="header-title">
        <h3>Assign Vehicle</h3>
        <span v-if="subtitle" class="subtitle">{{ subtitle }}</span>
      </div>
      <button type="button" class="close-btn" title="Close" @click="closeModal"><i class="fas fa-times"></i></button>
    </div>

    <div class="assign-body">
      <div class="assigned-block">
        <span v-for="(vehicle, vehicleIndex) in assignedVehicles" :key="vehicleIndex" class="vehicle-chip">
          <span>{{ vehicle.vehicleno }}</span>
          <button type="button" class="chip-remove" title="Unassign" @click="unlinkVehicle(vehicle)"><i class="fas fa-times"></i></button>
        </span>
        <span v-if="!assignedVehicles.length" class="no-vehicle">No vehicle assigned yet</span>
      </div>

      <label class="field-label">Select vehicle to assign <span class="req">*</span></label>
      <v-autocomplete
        v-model="vehicle"
        placeholder="Select vehicle"
        prepend-inner-icon="mdi-car"
        outlined
        dense
        :items="availableVehicles"
        return-object
        item-text="vehicleNo"
        hide-details
        class="vehicle-select"
        ref="vehicleSelect"
        :menu-props="menuProps"
        @focus="fitMenuToScreen"
        @click="fitMenuToScreen"
      >
        <template v-slot:item="{ item }">
          <div class="vehicle-option">
            <span class="option-no">{{ item.vehicleNo }}</span>
            <span class="option-meta">{{ [item.manufacturer, item.model].filter(Boolean).join(" ") }}</span>
            <span v-if="item.category" class="option-category">{{ item.category }}</span>
          </div>
        </template>
      </v-autocomplete>
    </div>

    <div class="assign-footer">
      <button type="button" class="btn btn--cancel" @click="closeModal">Cancel</button>
      <button type="button" class="btn btn--primary" :disabled="!vehicle || loading" @click="assignVehicle">Assign Vehicle</button>
    </div>

    <confirm ref="confirm"></confirm>
  </v-card>
</template>

<script>
import axios from "@/axios/axios-global";
import confirm from "@/components/common/Confirm";
export default {
  name: "VehicleAssign",
  components: { confirm },
  props: {
    driverDetails: Object,
  },
  data() {
    return {
      vehicle: null,
      vehicleList: [],
      loading: false,
      menuMaxHeight: 240,
      menuOpensUp: false,
    };
  },
  computed: {
    // open the list below the field, sized to the space left on screen
    menuProps() {
      if (this.menuOpensUp) return { offsetY: true, top: true, maxHeight: 240 };
      return { bottom: true, offsetY: true, offsetOverflow: false, allowOverflow: true, maxHeight: this.menuMaxHeight };
    },
    subtitle() {
      return [this.driverDetails.name, this.driverDetails.mobile].filter(Boolean).join(" · ");
    },
    assignedVehicles() {
      return this.driverDetails.vehicles || [];
    },
    // hide vehicles already linked to this driver
    availableVehicles() {
      const linked = this.assignedVehicles.map((v) => v.id);
      return this.vehicleList.filter((v) => !linked.includes(v.token));
    },
  },
  mounted() {
    this.getVehicleList();
  },
  methods: {
    fitMenuToScreen() {
      const field = this.$refs.vehicleSelect && this.$refs.vehicleSelect.$el;
      if (!field) return;
      const spaceBelow = window.innerHeight - field.getBoundingClientRect().bottom - 16;
      // less than ~3 rows below: open upwards instead
      this.menuOpensUp = spaceBelow < 150;
      this.menuMaxHeight = Math.max(150, Math.min(240, Math.floor(spaceBelow)));
    },
    async getVehicleList() {
      const response = await axios.post("vehicle/list", { is_pagination: false });
      this.vehicleList = response.data.list || [];
      if (this.vehicleList.length > 0) {
        this.vehicleList = this.vehicleList.filter((vehicle) => !vehicle.trash);
      }
    },
    async assignVehicle() {
      const payload = {
        vehicleNo: this.vehicle.vehicleNo,
        driver: {
          token: this.driverDetails.token,
          name: this.driverDetails.name,
        },
      };
      let confirmTitle = "Confirm Assign";
      let confirmText = `Please confirm to Assign ${this.vehicle.vehicleNo} to ${this.driverDetails.name}`;
      if (await this.$refs.confirm.open(confirmTitle, confirmText)) {
        this.loading = true;
        try {
          const response = await axios.patch("driver/vehicle/link", payload);
          if (response.status === 200) {
            if (!this.driverDetails.vehicles) this.$set(this.driverDetails, "vehicles", []);
            this.driverDetails.vehicles.push({ vehicleno: this.vehicle.vehicleNo, id: this.vehicle.token });
            this.vehicle = null;
            this.$toasted.show(response.data, {
              position: "bottom-center",
              duration: 4000,
            });
            this.$emit("update-driver");
          } else if (response.status === 206) {
            this.$toasted.show(response.data, {
              position: "bottom-center",
              duration: 4000,
            });
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      }
    },
    async unlinkVehicle(vehicle) {
      let confirmTitle = "Confirm UnAssign";
      let confirmText = `Please Confirm to Unassign ${vehicle.vehicleno} from ${this.driverDetails.name}`;
      let confirmIcon = "fas fa-exclamation";
      if (await this.$refs.confirm.open(confirmTitle, confirmText, { icon: confirmIcon, color: "red" })) {
        this.loading = true;
        try {
          const response = await axios.patch("driver/vehicle/unLink", {
            vehicleToken: vehicle.id,
            driverToken: this.driverDetails.token,
          });
          this.$toasted.show(response.data, {
            position: "bottom-center",
            duration: 4000,
          });
          if (response.status === 200) {
            const index = this.driverDetails.vehicles.findIndex((item) => item.id === vehicle.id);
            if (index > -1) this.driverDetails.vehicles.splice(index, 1);
          }
        } catch (error) {
          console.log("err", error);
        } finally {
          this.loading = false;
        }
      }
    },
    closeModal() {
      this.$emit("cancel-insert");
    },
  },
};
</script>

<style lang="scss" scoped>
$border: #e5e9f0;
$text: #1f2937;
$muted: #6b7280;

.assign-modal {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
}

.assign-header {
  display: flex;
  align-items: center;
  gap: 18px;
  margin: 0 28px;
  padding: 22px 0 18px;
  border-bottom: 1px solid $border;

  .header-icon {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 22px;
    color: #1976d2;
    background: #e8f0fe;
  }

  .header-title {
    flex: 1;
    min-width: 0;

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: $text;
    }

    .subtitle {
      font-size: 14px;
      color: $muted;
    }
  }

  .close-btn {
    align-self: flex-start;
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #374151;
    border-radius: 6px;

    &:hover {
      background: #f3f4f6;
    }
  }
}

.assign-body {
  padding: 20px 28px 24px;
}

.assigned-block {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 22px;

  .vehicle-chip {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 4px 6px 4px 16px;
    font-size: 15px;
    font-weight: 600;
    color: #1f6f8b;
    background: #e3f2fb;
    border-radius: 22px;
  }

  .chip-remove {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #e53935;
    background: #fff;
    border-radius: 50%;
    transition: background-color 0.15s;

    &:hover {
      background: #fde8e8;
    }
  }

  .no-vehicle {
    font-size: 14px;
    color: $muted;
  }
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 15px;
  color: #374151;

  .req {
    color: #e53935;
  }
}

::v-deep .vehicle-select {
  fieldset {
    border-color: #cfd6e1;
    border-radius: 6px;
  }

  .v-input__slot {
    min-height: 48px !important;
  }

  .v-input__prepend-inner {
    margin-top: 12px !important;
    margin-right: 8px;

    .v-icon {
      color: #374151;
    }
  }
}

.vehicle-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  .option-no {
    font-weight: 600;
    color: $text;
  }

  .option-meta {
    font-size: 13px;
    color: $muted;
  }

  .option-category {
    margin-left: auto;
    padding: 1px 8px;
    font-size: 12px;
    font-weight: 600;
    color: #1a5fd0;
    background: #e8f0fe;
    border-radius: 6px;
  }
}

.assign-footer {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  margin: 0 28px;
  padding: 18px 0 22px;
  border-top: 1px solid $border;

  .btn {
    height: 44px;
    padding: 0 24px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 6px;
    transition: filter 0.15s;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .btn--cancel {
    color: $text;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
  }

  .btn--primary {
    color: #fff;
    background: #1f5c55;
  }
}
</style>
